import {
  base_colorAction,
  base_colorContentBorder,
  base_colorContentBorderSecondary,
  base_colorSurfaceSecondary,
  notification_colorAlert,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  fontFormLabel,
  fontH2,
  fontH3,
  fontRegular,
  fontSupportMedium,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export const kybModalStyle = css`
  &.KybModal {
    display: flex;
    width: 100%;
    max-width: 100%;
    flex-direction: column;
    gap: ${spacing(6)};
    /* Lock the panel height; the middle (.KybModal-scroll) scrolls while the
       header + footer stay pinned. */
    height: min(74vh, 680px);
    overflow: hidden;
  }

  .KybModal-header {
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
    flex-shrink: 0;
  }

  /* The one scrolling region — fills the remaining locked height between the
     pinned header and footer, and scrolls its own overflow. On step change the
     step heading inside it receives focus, so keyboard users land in the region
     and arrow/Page keys scroll it — no tabIndex on the container itself (that
     trips jsx-a11y/no-noninteractive-tabindex and is redundant here). */
  .KybModal-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  .KybModal-title {
    ${fontH2};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  /* The heading receives focus on step change to announce the new step; keep a
     visible focus ring for that programmatic focus. */
  .KybModal-title:focus-visible {
    outline: 2px solid ${text_colorTextPrimary};
    outline-offset: 2px;
  }

  .KybModal-subtitle {
    ${fontRegular};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  /* The "before you start" info box (blue AlertPanel) at the top of step 1. Its
     title + body are composed into the panel's children (the panel's title
     prop is a native tooltip only), so they're styled here. Color is inherited
     from the panel's blue text — only the title's weight is set. */
  .KybModal-infoBox {
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
  }

  /* "Before you start" banner: monochrome treatment (neutral border + primary
     title + secondary body) over the soft surface, matching the prototype's
     soft+monochrome AlertPanel (ext-ui has no such variant — DS gap flagged for
     the ADS list). */
  .KybModal-infoPanel {
    /* !important to beat the ext-ui AlertPanel default variant's own blue
       border (generated CSS, single-class specificity, loaded after this). */
    border-color: ${base_colorContentBorder} !important;
  }

  .KybModal-infoBoxTitle {
    font-family: ${font_family_weight_medium};
    color: ${text_colorTextPrimary};
  }

  .KybModal-content {
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
  }

  .KybModal-fieldGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${spacing(4)};
  }

  /* Grid children default to min-width:auto and can push past the (fixed-width)
     popup body on narrow/split-screen widths — let them shrink, and collapse to
     a single column so nothing overflows horizontally. */
  .KybModal-fieldGrid > * {
    min-width: 0;
  }

  .KybModal-fieldGrid .span2 {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    .KybModal-fieldGrid {
      grid-template-columns: 1fr;
    }
  }

  .KybModal-sectionLabel {
    ${fontSupportMedium};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  /* Step 1 groups its fields into titled sections; a wider gap separates the
     sections than the fields within each (matching the prototype's 28 vs 16). */
  .KybModal-content.step1 {
    gap: ${spacing(7)};
  }

  .KybModal-section {
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
  }

  .KybModal-sectionTitle {
    ${fontRegular};
    font-family: ${font_family_weight_medium};
    margin: 0;
    color: ${text_colorTextPrimary};
    /* align an optional trailing TooltipIcon with the heading text */
    display: flex;
    align-items: center;
    gap: ${spacing(1)};
  }

  .KybModal-infoBoxList {
    margin: 0;
    padding-inline-start: ${spacing(4)};
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    list-style: disc;
    color: ${text_colorTextSecondary};
  }

  /* Red required asterisk composed into a field label that has no built-in
     requirement marker (e.g. SingleDateInputField's fieldLabel). */
  .KybModal-requiredMark {
    color: ${notification_colorAlert};
  }

  .KybModal-checkboxRow {
    display: flex;
    align-items: flex-start;
    gap: ${spacing(2)};
    ${fontSupportMedium};
    color: ${text_colorTextPrimary};
  }

  .KybModal-gateNote {
    ${fontSupportMedium};
    margin: 0;
    color: ${text_colorTextSecondary};
  }

  .KybModal-gateNote.alert {
    color: ${notification_colorAlert};
  }

  .KybModal-serverErrors {
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    ${fontSupportMedium};
  }

  .KybModal-serverErrors ul {
    margin: 0;
    padding-inline-start: ${spacing(4)};
  }

  .KybModal-footer {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing(3)};
    border-top: 1px solid ${base_colorContentBorderSecondary};
    padding-top: ${spacing(5)};
    flex-shrink: 0;
  }

  /* Lone action (submitting, success) — still right-aligned, where the primary
     action sits in every other step. Centering moved it. */
  .KybModal-footer.single {
    justify-content: flex-end;
  }

  .KybModal-result {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing(4)};
    /* Fill the locked scroll region so the status sits centered in the panel
       rather than hugging the header. */
    min-height: 100%;
    padding: ${spacing(6)} 0;
    text-align: center;
  }

  .KybModal-resultTitle {
    ${fontH3};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  .KybModal-resultText {
    ${fontSupportMedium};
    margin: 0;
    color: ${text_colorTextSecondary};
  }

  .KybModal-summary {
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    background-color: ${base_colorSurfaceSecondary};
    padding: ${spacing(6)};
  }

  .KybModal-summaryHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing(2)};
  }

  .KybModal-summaryTitle {
    ${fontH3};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  .KybModal-ownerSummaryList {
    display: flex;
    flex-direction: column;
    gap: ${spacing(5)};
  }

  .KybModal-ownerSummary {
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
  }

  .KybModal-ownerSummary + .KybModal-ownerSummary {
    border-top: 1px solid ${base_colorContentBorderSecondary};
    padding-top: ${spacing(5)};
  }

  .KybModal-ownerSummaryTitle {
    ${fontRegular};
    font-family: ${font_family_weight_medium};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  /* Rows within one person group tighter than the business card's list; the
     divider is what separates people. */
  .KybModal-ownerSummary .CardDetailList {
    gap: ${spacing(4)};
  }

  .KybModal-ownerList {
    display: flex;
    flex-direction: column;
    gap: ${spacing(5)};
  }

  .KybModal-ack {
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
  }

  .KybModal-ackRow {
    display: flex;
    align-items: flex-start;
    gap: ${spacing(3)};
    ${fontSupportMedium};
    color: ${text_colorTextPrimary};
  }
`

export const kybAddressStyle = css`
  &.KybAddress {
    display: flex;
    flex-direction: column;
    /* Legend-to-fields matches .KybModal-section's header-to-body gap so every
       heading in the modal sits the same distance above its content. */
    gap: ${spacing(4)};
    /* Reset the browser's default <fieldset> chrome (this is a semantic
       fieldset/legend group, styled from scratch). */
    margin: 0;
    padding: 0;
    border: 0;
    min-inline-size: 0;
  }

  /* The address group name reads as a section header (16px medium primary),
     matching the prototype and the step's other section titles — not a muted
     field label. */
  .KybAddress-legend {
    ${fontRegular};
    font-family: ${font_family_weight_medium};
    /* A legend sits outside its fieldset's flex flow, so the container gap
       never applies to it — the header-to-body spacing has to be its own
       margin. Matches .KybModal-section's gap. */
    margin: 0 0 ${spacing(4)};
    /* Reset the browser's default <legend> padding + let it span the row. */
    padding: 0;
    width: 100%;
    color: ${text_colorTextPrimary};
  }

  .KybAddress-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${spacing(4)};
  }

  .KybAddress-grid > * {
    min-width: 0;
  }

  .KybAddress-grid .span2 {
    grid-column: 1 / -1;
  }

  /* Country is locked to the US — render the lock affordance in action-blue to
     match the prototype (LockedIcon ships a hardcoded grey stroke, so override
     the path directly rather than relying on currentColor). */
  .KybAddress-lockIcon path {
    stroke: ${base_colorAction};
  }

  @media (max-width: 640px) {
    .KybAddress-grid {
      grid-template-columns: 1fr;
    }
  }
`

export const kybOwnerCardStyle = css`
  &.KybOwnerCard {
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    padding: ${spacing(6)};
  }

  .KybOwnerCard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing(2)};
  }

  .KybOwnerCard-title {
    ${fontH3};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  .KybOwnerCard-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${spacing(4)};
  }

  .KybOwnerCard-grid > * {
    min-width: 0;
  }

  .KybOwnerCard-grid .span2 {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    .KybOwnerCard-grid {
      grid-template-columns: 1fr;
    }
  }

  .KybOwnerCard-required {
    color: ${notification_colorAlert};
  }

  /* The control person's "also owns 25%+" checkbox row. */
  .KybOwnerCard-roleOption {
    display: flex;
    align-items: center;
    gap: ${spacing(2)};
    ${fontSupportMedium};
    color: ${text_colorTextPrimary};
  }
`

export const kybDocumentStyle = css`
  &.KybDocument {
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
  }

  .KybDocument-label {
    display: flex;
    align-items: center;
    gap: ${spacing(1)};
    ${fontFormLabel};
  }

  .KybDocument-required {
    color: ${notification_colorAlert};
  }

  .KybDocument-optional {
    ${fontSupportMedium};
    color: ${text_colorTextSecondary};
  }

  .KybDocument-selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing(2)};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    padding: ${spacing(3)} ${spacing(4)};
    background-color: ${base_colorSurfaceSecondary};
  }

  .KybDocument-selectedInfo {
    display: flex;
    align-items: center;
    gap: ${spacing(2)};
    min-width: 0;
  }

  .KybDocument-fileName {
    ${fontRegular};
    color: ${text_colorTextPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Dashed dropzone (click-to-browse or drag-and-drop), matching the prototype's
     DocumentUploader on ext-ui tokens. The whole box is the react-dropzone root
     (clickable + keyboard-accessible); the hidden input lives inside it. */
  .KybDocument-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing(2)};
    padding: ${spacing(6)} ${spacing(4)};
    border: 1px dashed ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    background-color: ${base_colorSurfaceSecondary};
    cursor: pointer;
    text-align: center;
    transition:
      border-color 0.15s ease-out,
      background-color 0.15s ease-out;
  }

  .KybDocument-dropzone:hover,
  .KybDocument-dropzone.dragActive {
    border-color: ${base_colorAction};
  }

  .KybDocument-dropzone:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
  }

  .KybDocument-dropzoneIcon path {
    stroke: ${base_colorAction};
  }

  .KybDocument-dropzoneText {
    ${fontSupportMedium};
    margin: 0;
    color: ${text_colorTextPrimary};
  }

  .KybDocument-dropzoneLink {
    color: ${base_colorAction};
    font-family: ${font_family_weight_medium};
  }

  .KybDocument-dropzoneHint {
    ${fontSupportMedium};
    margin: 0;
    color: ${text_colorTextSecondary};
  }

  .KybDocument-microcopy {
    ${fontSupportMedium};
    margin: 0;
    color: ${text_colorTextSecondary};
  }

  .KybDocument-error {
    ${fontSupportMedium};
    margin: 0;
    color: ${notification_colorAlert};
  }
`
